import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  CalendarCheck,
  Euro,
  KeyRound,
  LayoutDashboard,
  LogOut,
  Menu,
  Route,
  Users,
  X,
} from 'lucide-react';

import { Logo } from '@/components/logo.js';
import { useAuth } from '@/features/auth/hooks/use-auth.js';

export function AppShell(): JSX.Element {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = (): void => {
    logout();
    navigate('/login', { replace: true });
  };

  const closeMobile = (): void => {
    setMobileOpen(false);
  };

  const navItems =
    user?.rolle === 'disponent' ? (
      <>
        <NavItem
          to="/dashboard"
          icon={<LayoutDashboard size={16} />}
          label="Dashboard"
          onNavigate={closeMobile}
        />
        <NavItem to="/touren" icon={<Route size={16} />} label="Touren" onNavigate={closeMobile} />
        <NavItem to="/fahrer" icon={<Users size={16} />} label="Fahrer" onNavigate={closeMobile} />
        <NavItem
          to="/finanzen"
          icon={<Euro size={16} />}
          label="Finanzen"
          onNavigate={closeMobile}
        />
        <NavItem
          to="/login-info"
          icon={<KeyRound size={16} />}
          label="Login"
          onNavigate={closeMobile}
        />
      </>
    ) : user?.rolle === 'fahrer' ? (
      <>
        <NavItem
          to="/portal"
          icon={<Route size={16} />}
          label="Meine Touren"
          onNavigate={closeMobile}
        />
        <NavItem
          to="/portal/verfuegbarkeit"
          icon={<CalendarCheck size={16} />}
          label="Meine Verfügbarkeit"
          onNavigate={closeMobile}
        />
      </>
    ) : null;

  return (
    <div className="flex h-full flex-col md:flex-row">
      <header className="flex items-center justify-between border-b border-brand-line bg-white px-4 py-3 md:hidden">
        <Logo size="sm" />
        <button
          type="button"
          onClick={() => {
            setMobileOpen((v) => !v);
          }}
          aria-label="Menü"
          className="rounded-md p-2 text-brand-ink hover:bg-slate-50"
        >
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </header>

      {mobileOpen && (
        <nav className="border-b border-brand-line bg-white px-3 py-3 md:hidden">
          <div className="flex flex-col gap-1 text-sm">{navItems}</div>
          <button
            type="button"
            onClick={() => {
              closeMobile();
              handleLogout();
            }}
            className="mt-3 flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-brand-muted hover:bg-slate-50"
          >
            <LogOut size={16} />
            Abmelden
          </button>
        </nav>
      )}

      <aside className="hidden w-56 shrink-0 flex-col border-r border-brand-line bg-white px-4 py-6 md:flex">
        <div className="mb-8 px-2">
          <Logo size="md" />
        </div>
        <nav className="flex flex-col gap-1 text-sm">{navItems}</nav>
        <button
          type="button"
          onClick={handleLogout}
          className="mt-auto flex items-center gap-2 rounded-md px-3 py-2 text-sm text-brand-muted hover:bg-slate-50"
        >
          <LogOut size={16} />
          Abmelden
        </button>
      </aside>

      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}

interface NavItemProps {
  to: string;
  icon: JSX.Element;
  label: string;
  onNavigate?: () => void;
}

function NavItem({ to, icon, label, onNavigate }: NavItemProps): JSX.Element {
  return (
    <NavLink
      to={to}
      onClick={onNavigate}
      className={({ isActive }) =>
        `flex items-center gap-2 rounded-md px-3 py-2 ${
          isActive ? 'bg-brand-ink text-white' : 'text-brand-ink hover:bg-slate-50'
        }`
      }
    >
      {icon}
      {label}
    </NavLink>
  );
}
